<template>
  <div class='work-flow-ticket' @click.stop='toggleWorkflowTicketPopup'>
    <div class='work-flow-ticket__top'>
      <div class='work-flow-ticket__top__name' :title='name'>
        {{ name }}
      </div>
      <div class='work-flow-ticket__top__info'>
        <custom-tooltip class='work-flow-ticket__top__info__tooltip' placement="right" popper-class="light-tooltip">
          <div slot='buttonContent'>
            <v-btn class='info-btn'
                   fab
                   large
                   :ripple="false"
                   @click.stop='toggleInfoPopup'
            >
              <v-icon class="material-icons-outlined">info</v-icon>
            </v-btn>
          </div>
          <div slot="content">
            <slot name="tooltip">
              <span>Info</span>
            </slot>
          </div>
        </custom-tooltip>
      </div>
    </div>
    <div class='work-flow-ticket__content'>
      <div class='work-flow-ticket__content__execution-amount'>
        <div class='work-flow-ticket__content__execution-amount__title'>
          {{ $t('work.flow.executed.title') }}
        </div>
        <div class='work-flow-ticket__content__execution-amount__value'>
          {{ executionAmount }}
        </div>
      </div>
      <div class='work-flow-ticket__content__performance-score'>
        <div class='work-flow-ticket__content__performance-score__title'>
          {{ $t('workflows.layout.performance.score.title') }}
        </div>
        <div class='work-flow-ticket__content__performance-score__value'>
          {{ performanceScoreFormatted }}
        </div>
      </div>
      <div class='work-flow-ticket__content__associated-tld'>
        <div class='work-flow-ticket__content__associated-tld__title'>
          {{ $t('workflows.layout.associated.tlds.title') }}
        </div>
        <div class='work-flow-ticket__content__associated-tld__value'>
          {{ associatedTldsAmount }}
        </div>
      </div>
    </div>
    <div class='work-flow-ticket__source'>
      <div class='work-flow-ticket__source__toggle' @click.stop>
        <v-switch
          v-model="switchValue"
          inset
        />
      </div>
      <v-avatar>
        <company-logo :domain="whiteLabelingConfig.domain" view="no_text" v-if="whiteLabelingConfig.domain"/>
        <cynergy-app-no-text v-else/>
      </v-avatar>
    </div>
  </div>
</template>

<script>
import CynergyAppNoText from '@/assets/icons/whitelabeling/cynergy.app/cynergy_app_no_text.svg?inline';
import CustomTooltip from '@/components/common/CustomTooltip.vue';
import CompanyLogo from '@/components/common/CompanyLogo.vue';
import {mapGetters} from 'vuex';

export default {
  name: 'WorkFlowTicket',
  props: {
    workFlow: {
      required: true,
      type: Object,
    },
  },
  data() {
    return {
      switchValue: this.workFlow?.status === 'enabled' || false,
    };
  },
  components: {
    CompanyLogo,
    CustomTooltip,
    CynergyAppNoText,
  },
  computed: {
    ...mapGetters(['whiteLabelingConfig']),
    performanceScore() {
      return this.workFlow.performanceScore || 0;
    },
    performanceScoreFormatted() {
      return ` ${this.performanceScore}`;
    },
    associatedTldsAmount() {
      return this.workFlow.domainsCovered || 0;
    },
    executionAmount() {
      return this.workFlow.executionAmount || 0;
    },
    name() {
      return this.workFlow.name || `Workflow #${this.id + 1}`;
    },
    id() {
      return this.workFlow.id;
    },
    description() {
      return this.workFlow.description || 'N/A';
    },
  },
  watch: {
    async switchValue() {
      await this.$emit('enabledChanged', this.id);
    },
  },
  methods: {
    toggleInfoPopup() {
      this.$emit('toggleInfoPopup', this.id);
    },
    toggleWorkflowTicketPopup() {
      this.$emit('toggleWorkflowTicketPopup', this.id);
    },
  },
};
</script>

<style lang='scss' scoped>
@import '~@/assets/styles/design-system';

.work-flow-ticket {
  display: flex;
  flex-direction: column;
  min-width: rem(270);
  max-width: rem(270);
  min-height: rem(205);
  max-height: rem(205);
  padding: rem(20);
  flex-grow: 1;
  flex-shrink: 1;
  border-radius: $radius-md;
  background-color: var(--bg-secondary);
  .v-btn--round {
    border-radius: 50% !important;
    color: var(--brand-primary);
    font-size: rem(24);
  }
  .info-btn {
    background-color: var(--button-good-bg) !important;
    box-shadow: none;
    font-family: $font-family-medium;
    text-align: center;
    min-width: rem(24);
    max-width: rem(24);
    min-height: rem(24);
    max-height: rem(24);
    color: var(--button-good-text);
    .v-icon {
      font-size: rem(24);
    }
    &:hover {
      cursor: pointer;
      border: var(--button-good-hover-border);
      background-color: var(--button-good-hover-bg) !important;
    }
    &:active{
      border: var(--button-good-active-border);
      background-color: var(--button-good-hover-bg) !important;
    }
    &:before{
      color: transparent;
    }
  }
  &__top {
    display: flex;
    justify-content: space-between;
    margin-bottom: rem(10);
    &__name {
      text-transform: capitalize;
      color: var(--text-primary);
      letter-spacing: 0;
      font-family: $font-family-bold;
      font-size: rem(16);
      line-height: rem(20);
      white-space: nowrap;
      word-wrap: break-word;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-right: rem(5);
    }
    &__info {
      z-index: 2;
      &__tooltip {
        background-color: transparent;
      }
      &:hover {
        cursor: pointer;
      }
    }
  }
  &__content {
    color: var(--text-primary);
    &__execution-amount {
      margin-bottom: rem(5);
      display: flex;
      font-family: $font-family-regular;
      font-size: rem(12);
      letter-spacing: 0;
      line-height: rem(15);
      &__title {
        margin-right: rem(3);
      }
      &_value {
        font-weight: bold;
      }
    }
    &__performance-score {
      margin-bottom: rem(5);
      display: flex;
      font-family: $font-family-regular;
      font-size: rem(12);
      letter-spacing: 0;
      line-height: rem(15);
      &__title {
        margin-right: rem(3);
      }
      &_value {
        font-weight: bold;
      }
    }
    &__associated-tld {
      margin-bottom: rem(5);
      display: flex;
      font-family: $font-family-regular;
      font-size: rem(12);
      letter-spacing: 0;
      line-height: rem(15);
      &__title {
        margin-right: rem(3);
      }
      &_value {
        font-weight: bold;
      }
    }
  }
  &__source {
    justify-content: space-between;
    flex-direction: row;
    display: flex;
    align-items: center;
    margin-top: rem(28);
    &__toggle{
      z-index: 3;
    }
    .v-avatar {
      height: rem(30) !important;
      margin-right: rem(-15);
      // Ensure logo is visible on dark card backgrounds
      svg {
        fill: var(--text-secondary);
      }
    }
  }
  &:hover {
    cursor: pointer;
  }
}
</style>
